import {
	PERFORMANCE_STATUS,
	PERFORMANCE_LEVELS,
	getChartConfig,
	getLoadingChartConfig,
	getRandomTitle,
	getCompetitorColor,
	formatCurrency
} from '../Helpers/Helpers';
import { Flex, Text, Select, Icon, Tooltip, Help, Title, AssistChip } from '@upsales/components';
import { SelectItem } from '@upsales/components/Utils/selectHelpers';
import { CancelablePromise, makeCancelable } from 'Helpers/promise';
import ClientIndustryResource from 'Resources/ClientIndustry';
import React, { useEffect, useRef, useState } from 'react';
import BemClass from '@upsales/components/Utils/bemClass';
import { useIndustryInsights } from '../Context/Context';
import IconName from '@upsales/components/Icon/IconName';
import EmptyData from '../EmptyData/EmptyData';
import T from 'Components/Helpers/translate';
import logError from 'Helpers/logError';
import Highcharts from 'highcharts';
import './ClientCompetitor.scss';

interface TimeSeriesData {
	dateFrom: string;
	dateTo: string;
	ruleOfForty: number;
	ruleOfFortyMedian: number;
	ruleOfFortyUpperQuartile: number;
	ruleOfFortyLowerQuartile: number;
	bucketRange: {
		minRev: number;
		maxRev: number;
	};
}
interface ClientIndustryResponse {
	importDate: string;
	similarCompaniesCount: number;
	timeSeries: TimeSeriesData[];
}

interface CompetitorMetrics {
	name: string;
	data: {
		dateFrom: string;
		dateTo: string;
		ruleOfForty: number;
	}[];
}

interface Performance {
	title: string;
	label: string;
	type: 'default' | 'info' | 'purple' | 'green' | 'success' | 'danger' | 'alert' | undefined;
	icon: IconName | undefined;
}

interface Props {
	clientId: number;
	clientName: string;
	code: string;
}

const ClientCompetitor = ({ clientId, clientName, code }: Props) => {
	const classes = new BemClass('ClientCompetitor');
	const chartRef = useRef<HTMLDivElement>(null);
	const [performance, setPerformance] = useState<Performance>({
		title: '',
		label: '',
		type: 'default',
		icon: undefined
	});
	const {
		state: { bucketRange, description, competitorLoading, hasTimeSeriesData },
		setHasTimeSeriesData,
		setBucketRange,
		setImportDate,
		setCompetitorLoading
	} = useIndustryInsights();
	const [yearRange, setYearRange] = useState(5);
	const [availableYears, setAvailableYears] = useState<number[]>([]);
	const [selectedYearOption, setSelectedYearOption] = useState<SelectItem>({ id: 5, title: T('date.prev5Year') });
	const [similarCompaniesCount, setSimilarCompaniesCount] = useState<number>(0);

	const companyDataRequest = useRef<null | CancelablePromise<{ data: ClientIndustryResponse }>>(null);
	const competitorDataRequest = useRef<null | CancelablePromise<{ data: CompetitorMetrics[] }>>(null);

	const YEAR_RANGE_OPTIONS = [
		{ years: 3, key: 'lastThreeYears' as const },
		{ years: 5, key: 'lastFiveYears' as const },
		{ years: 10, key: 'lastTenYears' as const }
	];

	const lang = {
		companyGrowthComparison: T('ai.industryInsights.companyGrowthComparison'),
		similarCompanies: T('default.similarCompanies'),
		similarCompaniesCount: T('default.similarCompaniesCount', { count: similarCompaniesCount }),
		inIndustry: T('default.inIndustry'),
		lastThreeYears: T('date.prev3Year'),
		lastFiveYears: T('date.prev5Year'),
		lastTenYears: T('date.prev10Year'),
		allYears: T('date.prevAvailableYear'),
		howItWorks: T('ai.industryInsights.howItWorks'),
		highestPerformerGraph: T('ai.industryInsights.highestPerformerGraph'),
		averagePerformerGraph: T('ai.industryInsights.averagePerformerGraph'),
		underperformerGraph: T('ai.industryInsights.underperformerGraph'),
		similarCompaniesTooltip: T('ai.industryInsights.similarCompaniesTooltip', {
			value: bucketRange ? `${formatCurrency(bucketRange.minRev)} - ${formatCurrency(bucketRange.maxRev)}` : '',
			industry: description
		})
	};

	const getYearRangeOptions = (totalYears: number): SelectItem[] => {
		const options: SelectItem[] = [];

		for (const option of YEAR_RANGE_OPTIONS) {
			if (totalYears > option.years) {
				options.push({ id: option.years, title: lang[option.key] });
			} else if (totalYears === option.years) {
				options.push({ id: totalYears, title: lang.allYears });
			}
		}

		if (totalYears > Math.max(...YEAR_RANGE_OPTIONS.map(opt => opt.years))) {
			options.push({ id: totalYears, title: lang.allYears });
		}

		return options;
	};

	const filterDataByYearRange = (data: TimeSeriesData[], years: number) => {
		if (!data.length) return data;
		return data.slice(-years);
	};

	const processYearlyData = (competitorData: CompetitorMetrics['data'], years: string[]) => {
		// Return array of values aligned with years array
		// If a year has no data but there's data before and after, use null to allow connecting
		// If there's no data before or after, use undefined to prevent connecting
		return years.map(year => {
			const dataPoint = competitorData.find(d => new Date(d.dateFrom).getFullYear().toString() === year);
			if (dataPoint) {
				return dataPoint.ruleOfForty;
			}
			return null;
		});
	};

	const updatePerformanceStatus = (latestData: TimeSeriesData) => {
		const currentValue = latestData.ruleOfForty;
		const upperQuartile = latestData.ruleOfFortyUpperQuartile;
		const median = latestData.ruleOfFortyMedian;
		const lowerQuartile = latestData.ruleOfFortyLowerQuartile;
		const latestPerformanceYear = new Date(latestData.dateFrom).getFullYear();

		if (currentValue >= upperQuartile) {
			setPerformance({
				title: T(getRandomTitle('TOP_PERFORMER'), { client: clientName, year: latestPerformanceYear }),
				label: T('ai.industryInsights.highestPerformer'),
				...PERFORMANCE_STATUS.TOP_PERFORMER
			});
		} else if (currentValue >= median) {
			setPerformance({
				title: T(getRandomTitle('ABOVE_AVERAGE'), { client: clientName, year: latestPerformanceYear }),
				label: T('ai.industryInsights.aboveAveragePerformer'),
				...PERFORMANCE_STATUS.ABOVE_AVERAGE
			});
		} else if (currentValue >= lowerQuartile) {
			setPerformance({
				title: T(getRandomTitle('AVERAGE'), { client: clientName, year: latestPerformanceYear }),
				label: T('ai.industryInsights.averagePerformer'),
				...PERFORMANCE_STATUS.AVERAGE
			});
		} else {
			setPerformance({
				title: T(getRandomTitle('UNDERPERFORMER'), { client: clientName, year: latestPerformanceYear }),
				label: T('ai.industryInsights.underperformer'),
				...PERFORMANCE_STATUS.UNDERPERFORMER
			});
		}
	};

	const initLoadingChart = () => {
		if (chartRef.current) {
			const loadingOptions = getLoadingChartConfig({
				clientName,
				lang,
				yearRange
			});
			Highcharts.chart(chartRef.current, loadingOptions);
		}
	};

	useEffect(() => {
		if (competitorLoading) {
			initLoadingChart();
		}
	}, [competitorLoading]);

	useEffect(() => {
		const shouldSkipDataFetch = !clientId;
		if (shouldSkipDataFetch) {
			setCompetitorLoading(false);
			return;
		}
		setCompetitorLoading(true); // Will trigger the other useEffect

		const requests = {
			companyData: makeCancelable(ClientIndustryResource.getClientIndustryStatistics(clientId)),
			competitors: makeCancelable(ClientIndustryResource.getCompetitorRuleOfForty(clientId))
		};

		companyDataRequest.current = requests.companyData;
		competitorDataRequest.current = requests.competitors;

		Promise.all([requests.companyData.promise, requests.competitors.promise])
			.then(([companyData, competitors]) => {
				const timeSeries = companyData?.data?.timeSeries || [];
				if (timeSeries.length > 0 && competitors?.data.length > 0 && chartRef.current) {
					const { importDate, similarCompaniesCount: companiesCount } = companyData.data;
					const years = timeSeries.map((d: TimeSeriesData) => new Date(d.dateFrom).getFullYear());
					setAvailableYears(years);

					const filteredData = filterDataByYearRange(timeSeries, yearRange);
					const filteredYears = filteredData.map(d => new Date(d.dateFrom).getFullYear().toString());
					const yearOption = {
						id: yearRange,
						title: YEAR_RANGE_OPTIONS.find(opt => opt.years === yearRange)?.key
							? lang[YEAR_RANGE_OPTIONS.find(opt => opt.years === yearRange)!.key]
							: lang.allYears
					};

					const top3Competitors = competitors.data.map((competitor: CompetitorMetrics, index: number) => ({
						name: competitor.name,
						type: 'spline',
						data: processYearlyData(competitor.data, filteredYears),
						color: getCompetitorColor(index),
						visible: false,
						zIndex: 4 + index,
						marker: { symbol: 'diamond' }
					}));

					const options = getChartConfig();
					(options.xAxis as Highcharts.AxisOptions).categories = filteredYears;
					options.series = [
						...PERFORMANCE_LEVELS.map(level => ({
							name: lang[level.name],
							type: 'areaspline',
							data: filteredData.map(d => {
								const hasAllValues =
									d.ruleOfFortyUpperQuartile != null &&
									d.ruleOfFortyMedian != null &&
									d.ruleOfFortyLowerQuartile != null;
								return hasAllValues ? d[level.dataKey] : null;
							}),
							color: level.color,
							zIndex: level.zIndex,
							threshold: null,
							fillColor: {
								linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
								stops: [
									[0, `${level.color}80`],
									[1, `${level.color}33`]
								]
							}
						})),
						{
							name: clientName,
							type: 'spline',
							data: filteredData.map(d => d.ruleOfForty || null),
							color: '#000000',
							zIndex: 4,
							marker: { symbol: 'circle' }
						},
						...top3Competitors
					];

					Highcharts.chart(chartRef.current, options);
					setHasTimeSeriesData(true);
					setBucketRange({
						minRev: timeSeries[0].bucketRange.minRev,
						maxRev: timeSeries[0].bucketRange.maxRev
					});
					setSimilarCompaniesCount(companiesCount);
					setSelectedYearOption(yearOption);
					setImportDate(importDate);
					updatePerformanceStatus(filteredData[filteredData.length - 1]);
				}
			})
			.catch(error => {
				logError('Failed to load ClientCompetitor data:', error);
				setHasTimeSeriesData(false);
			})
			.finally(() => {
				setCompetitorLoading(false);
			});

		return () => {
			Object.values(requests).forEach(request => request.cancel());
		};
	}, [clientId, code, yearRange]);

	const renderChart = () => {
		if (competitorLoading) {
			return (
				<Flex className={classes.elem('chartWidgetContainer').b()} gap="u2">
					<Flex
						className={classes.elem('chartWidget').b()}
						direction="column"
						space="ptl prl pbl pll"
						gap="u4"
					>
						<Flex justifyContent="space-between" alignItems="center">
							<Flex alignItems="center" justifyContent="space-between" flex={1}>
								<Flex gap="u2" alignItems="center">
									<Text loading bold>
										{lang.companyGrowthComparison} {lang.similarCompaniesCount}{' '}
										{lang.inIndustry.toLowerCase()}
									</Text>
									<Icon name="info-circle" loading />
								</Flex>
								<Text loading size="sm" />
							</Flex>
						</Flex>
						<Flex flex={1} ref={chartRef} className={classes.elem('chart').b()} />
					</Flex>
				</Flex>
			);
		}

		if (!hasTimeSeriesData) {
			return (
				<Flex className={classes.elem('chartWidgetContainer').b()} gap="u2">
					<Flex
						className={classes.elem('chartWidget').b()}
						direction="column"
						space="ptl prl pbl pll"
						gap="u4"
					>
						<EmptyData />
					</Flex>
				</Flex>
			);
		}

		return (
			<Flex className={classes.elem('chartWidgetContainer').b()} gap="u2">
				<Flex className={classes.elem('chartWidget').b()} direction="column" space="ptl prl pbl pll" gap="u4">
					<Flex alignContent="space-between" justifyContent="center" direction="column">
						<Flex alignItems="center" justifyContent="space-between" flex={1}>
							<Flex gap="u2" alignItems="center">
								<Text bold>
									{lang.companyGrowthComparison} {lang.similarCompaniesCount}{' '}
									{lang.inIndustry.toLowerCase()}
								</Text>
								<Tooltip title={lang.similarCompaniesTooltip}>
									<Icon name="info-circle" />
								</Tooltip>
							</Flex>
							<Select
								className={classes.elem('yearSelect').b()}
								anchor={'.Modals'}
								size="sm"
								showSearch={false}
								value={selectedYearOption}
								onChange={option => {
									setCompetitorLoading(true);
									setSelectedYearOption(option);
									setYearRange(Number(option.id));
								}}
								options={getYearRangeOptions(availableYears.length)}
							/>
						</Flex>
						<Flex>
							<Help articleId={1557}>
								<Text size="sm" color="bright-blue">
									{lang.howItWorks}
								</Text>
							</Help>
						</Flex>
					</Flex>
					<Flex flex={1} ref={chartRef} className={classes.elem('chart').b()} />
				</Flex>
			</Flex>
		);
	};

	return (
		<Flex direction="column" gap="u4" className={classes.b()}>
			<Title bold size="lg" loading={competitorLoading}>
				{hasTimeSeriesData
					? performance.title
					: T('default.noResultsForClientWithType', { client: clientName, type: T('default.e40') })}
			</Title>
			<Flex className={classes.elem('pendoWrapper').b()} justifyContent="space-between">
				<Tooltip title={T('ai.industryInsights.performerGraphTitle')}>
					<AssistChip
						title={
							hasTimeSeriesData
								? performance.label
								: competitorLoading
								? T('default.loadingEllipsis')
								: T('default.noResults')
						}
						type={hasTimeSeriesData ? performance.type : 'default'}
						icon={hasTimeSeriesData ? performance.icon : undefined}
					/>
				</Tooltip>
			</Flex>
			{renderChart()}
		</Flex>
	);
};

export default ClientCompetitor;
